import moment from "moment";
import { db, storage } from "../../connection";
import { collection, doc, getDocs, updateDoc } from "@firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "@firebase/storage";
import logger from "../../../../utils/logger";
import dayjs from "dayjs";
import { chantApi } from "../../../../utils/chantApi";

export function getImages(groupId) {
  return new Promise((resolve, reject) => {
    getDocs(collection(db, "gallery", groupId, "images"))
      .then((snap) => {
        let data = [];
        for (var doc of snap.docs) {
          let dimensions = (doc.data() || {}).dimensions || {
            height: 320,
            width: 320,
          };
          let { width, height } = dimensions;
          let imageHeight = 0,
            imageWidth = 0;
          if (height > width) {
            imageHeight = 480;
            imageWidth = (width / height) * imageHeight;
          } else {
            imageWidth = 480;
            imageHeight = (height / width) * imageWidth;
          }
          let date = moment
            .unix((doc.data() || {}).imageDate || 0)
            .format("MMM DD YYYY");

          data.push({
            ...(doc.data() || {}),
            src: (doc.data() || {}).imageUrl,
            id: doc.id,
            groupId: groupId,
            width: imageWidth,
            height: imageHeight,
            tags: [{ value: date, title: date }],
          });
        }
        resolve(data);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export function changeShowOnWeb(show, imageId, groupId) {
  return new Promise((resolve, reject) => {
    updateDoc(doc(db, "gallery", groupId, "images", imageId), {
      showOnWeb: show,
    })
      .then(() => {
        resolve();
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export async function updateSizeOfImages() {
  let startingId = "g25rBv3js0L18UCSX4tX";
  let collectionRef = collection(db, "gallery");

  let groupId = startingId;
  if (!startingId || groupId === startingId) {
    startingId = "";
    let subCollectionRef = collection(
      db,
      collectionRef.path,
      groupId,
      "images",
    );
    let images = await getDocs(subCollectionRef);
    if (images.docs.length > 0) {
      for (var image of images.docs) {
        let url = (image.data() || {}).imageUrl;
        if (url) {
          const dimensions = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
              resolve({
                width: img.width,
                height: img.height,
              });
            };
            img.onerror = (err) => reject(err);
            img.src = url;
          });

          await updateDoc(image.ref, {
            dimensions,
          });
        }
      }
    }
  }
}

export function uploadImage(file, path, timestamp, imageDate) {
  return new Promise((resolve, reject) => {
    const folderRef = ref(storage, `${path}`);
    uploadBytes(folderRef, file)
      .then(async (snapshot) => {
        const downloadUrl = await getDownloadURL(snapshot.ref);
        resolve({ downloadUrl, imageDate, timestamp });
      })
      .catch(reject);
  });
}

export async function uploadImageSizes(
  smallFile,
  largeFile,
  path,
  timestamp,
  imageDate,
) {
  const fileExtension = smallFile?.type?.split("/")?.pop() || "jpg";
  const smallFolderRef = ref(storage, `${path}-sm.${fileExtension}`);
  const largeFolderRef = ref(storage, `${path}-lg.${fileExtension}`);

  const [snapshotSmall, snapshotLarge] = await Promise.all([
    uploadBytes(smallFolderRef, smallFile),
    uploadBytes(largeFolderRef, largeFile),
  ]);

  const [downloadUrlSmall, downloadUrlLarge] = await Promise.all([
    getDownloadURL(snapshotSmall.ref),
    getDownloadURL(snapshotLarge.ref),
  ]);

  return {
    downloadUrlSmall,
    downloadUrlLarge,
    imageDate,
    timestamp,
  };
}

export const fetchAdminGalleryPagedImages = async ({
  groupId,
  dateRange,
  pageParam = null,
}) => {
  try {
    if (!groupId) {
      return { images: [], hasMore: false, lastDocId: null };
    }

    const params = {
      groupId,
      limit: 20,
      lastDocId: pageParam?.lastDocId || null,
      startDate: dateRange?.[0]
        ? dayjs(dateRange[0]).startOf("month").unix()
        : null,
      endDate: dateRange?.[1]
        ? dayjs(dateRange[1]).endOf("month").unix()
        : null,
    };

    // Remove null params
    Object.keys(params).forEach((key) => {
      if (params[key] === null) delete params[key];
    });

    const response = await chantApi.get(
      `${import.meta.env.VITE_GCP_BASE_URL}/gallery/images`,
      { params },
    );

    const responseData = response.data?.data || {};
    const pagination = responseData.pagination || {};

    return {
      images: responseData.images || [],
      hasMore: pagination.hasMore || false,
      lastDocId: pagination.lastDocId || null,
    };
  } catch (error) {
    logger.error("Error fetching admin gallery images:", error);
    throw error;
  }
};
